// src/pages/dashboard/UsersPage.tsx
import React, { useState } from "react";
import {
  ShieldCheck,
  Search,
  Loader2,
  UserPlus,
  Trash2,
  X,
} from "lucide-react";
import {
  useGetAdminUsersQuery,
  useCreateAdminUserMutation,
  useUpdateAdminUserMutation,
  useDeleteAdminUserMutation,
  AdminUser,
} from "../../app/api/adminApi";
import { useAppSelector } from "../../app/hooks";
import { selectCurrentUser } from "../../app/features/authSlice";

function cn(...c: Array<string | false | null | undefined>) {
  return c.filter(Boolean).join(" ");
}
const fmtDate = (d?: string) =>
  d
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(new Date(d))
    : "—";

const errMsg = (e: any) =>
  e?.data?.error || e?.data?.message || e?.error || "Something went wrong";

const CreateUserModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [create, { isLoading }] = useCreateAdminUserMutation();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    role: "user" as "user" | "admin",
  });
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      await create(form).unwrap();
      onClose();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Add team member</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <input
            required
            placeholder="First name"
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
            className="rounded-xl border border-slate-200 p-2.5 text-sm outline-none focus:border-violet-300"
          />
          <input
            required
            placeholder="Last name"
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            className="rounded-xl border border-slate-200 p-2.5 text-sm outline-none focus:border-violet-300"
          />
        </div>
        <input
          required
          type="email"
          placeholder="Email address"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="mt-3 w-full rounded-xl border border-slate-200 p-2.5 text-sm outline-none focus:border-violet-300"
        />
        <input
          required
          type="password"
          placeholder="Temporary password (min 6 chars)"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="mt-3 w-full rounded-xl border border-slate-200 p-2.5 text-sm outline-none focus:border-violet-300"
        />
        <div className="mt-3 flex gap-2">
          {(["user", "admin"] as const).map((r) => (
            <button
              type="button"
              key={r}
              onClick={() => setForm({ ...form, role: r })}
              className={cn(
                "flex-1 rounded-xl border px-3 py-2 text-sm font-semibold capitalize transition",
                form.role === r
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 text-slate-600"
              )}
            >
              {r}
            </button>
          ))}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="mt-5 w-full rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
        >
          {isLoading ? "Creating…" : "Create account"}
        </button>
      </form>
    </div>
  );
};

const UsersPage: React.FC = () => {
  const me = useAppSelector(selectCurrentUser);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, isFetching } = useGetAdminUsersQuery({
    q: q.trim() || undefined,
    role,
    limit: 100,
  });
  const [updateUser] = useUpdateAdminUserMutation();
  const [deleteUser] = useDeleteAdminUserMutation();

  const users = data?.data?.users || [];

  const toggleRole = async (u: AdminUser) => {
    try {
      await updateUser({
        id: u._id,
        role: u.role === "admin" ? "user" : "admin",
      }).unwrap();
    } catch (e) {
      alert(errMsg(e));
    }
  };

  const remove = async (u: AdminUser) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await deleteUser({ id: u._id }).unwrap();
    } catch (e) {
      alert(errMsg(e));
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900">
          <ShieldCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Team &amp; users
          </h1>
          <p className="text-sm text-slate-500">
            Manage platform accounts and admin access.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="ml-auto flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <UserPlus className="h-4 w-4" /> Add member
        </button>
      </div>

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm shadow-sm outline-none focus:border-violet-300"
          />
        </div>
        <div className="flex gap-1.5">
          {["all", "admin", "user"].map((r) => (
            <button
              key={r}
              onClick={() => setRole(r)}
              className={cn(
                "rounded-xl border px-3.5 py-2 text-xs font-semibold capitalize",
                role === r
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-600"
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20 text-slate-400">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Verified</th>
                  <th className="px-4 py-3 font-semibold">Joined</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = me?.email === u.email;
                  return (
                    <tr key={u._id} className="border-b border-slate-50 last:border-0">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">
                          {u.firstName} {u.lastName}
                          {isSelf && (
                            <span className="ml-2 text-xs text-slate-400">(you)</span>
                          )}
                        </p>
                        <p className="text-xs text-slate-400">{u.email}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "rounded-full border px-2 py-0.5 text-xs font-semibold capitalize",
                            u.role === "admin"
                              ? "border-violet-200 bg-violet-50 text-violet-700"
                              : "border-slate-200 bg-slate-50 text-slate-500"
                          )}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {u.isEmailVerified ? (
                          <span className="text-emerald-600">Verified</span>
                        ) : (
                          <span className="text-slate-400">Unverified</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {fmtDate(u.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => toggleRole(u)}
                            disabled={isSelf}
                            className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                          >
                            {u.role === "admin" ? "Make user" : "Make admin"}
                          </button>
                          <button
                            onClick={() => remove(u)}
                            disabled={isSelf}
                            className="rounded-lg border border-red-200 p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-40"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!users.length && (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-slate-400">
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isFetching && !isLoading && (
        <p className="mt-3 text-xs text-slate-400">Refreshing…</p>
      )}

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} />}
    </div>
  );
};

export default UsersPage;
